import { Controller } from "@hotwired/stimulus";
import Swal from "sweetalert2";

export default class extends Controller {
    static targets = [
        "form",
        "percentList",
        "charityPercent",
        "giftPercent",
        "groupGifting", // contribution message
        "groupGift",    // actual gift
        "moreInfo",
        "optionCheckbox",
        "continueBtn",
        "giftsInline",
        "previewContributionMessage",
        "previewGiftLine",
        "bannerTextContainer"
    ];

    static values = {
        draftId: Number,
        draftSgid: String,
        invitationSlug: String,
        fields: Object,
        context: { type: String, default: "draft" },
        partyToken: String
    };

    pendingPatch = null;
    _isFlushing = false;
    _suspendAutosave = false;
    _flushAgain = false;

    connect() {
        // runtime flags
        this.pendingPatch = null;
        this._isFlushing = false;
        this._suspendAutosave = false;

        // prime local cache from data-* (or empty)
        this._cachedFields =
            (this.hasFieldsValue && this.fieldsValue)
                ? JSON.parse(JSON.stringify(this.fieldsValue))
                : {};

        // set up debounced flush for staged patches
        this._setupAutosave(); // defines this._debouncedFlush

        // bind inputs (percent buttons already use data-action="additional-info#pickPercent")
        this.groupGiftingTarget?.addEventListener('input', this.onGroupGiftingInput.bind(this));
        this.groupGiftingTarget?.addEventListener('blur', () => this._debouncedFlush());

        this.moreInfoTarget?.addEventListener('input', this.onMoreInfoInput.bind(this));
        this.moreInfoTarget?.addEventListener('blur', () => this._debouncedFlush());

        // this.groupGiftTarget?.addEventListener('input', this.onGroupGiftInput.bind(this));
        // this.groupGiftTarget?.addEventListener('blur', () => this._debouncedFlush());

        this.element.querySelectorAll('input[name="party_option_ids[]"]').forEach(cb => {
            cb.addEventListener('change', this.onOptionToggle.bind(this));
        });

        // paint UI from cache without triggering autosave
        this._rehydrateUIFrom(this._cachedFields);
    }

    // Stage a delta (no immediate network). We'll flush on blur or explicit triggers.
    _stagePatch(patch) {
        if (!patch || typeof patch !== 'object') return;
        // Deep clone arrays to avoid in-place mutation bugs
        const cloneArrays = (obj) => {
            if (Array.isArray(obj)) return obj.slice();
            if (obj && typeof obj === 'object') {
                const out = {};
                for (const [k, v] of Object.entries(obj)) out[k] = cloneArrays(v);
                return out;
            }
            return obj;
        };
        const safe = cloneArrays(patch);
        this.pendingPatch = this._mergeDeep(this.pendingPatch || {}, safe);
        this._cachedFields = this._mergeDeep(this._cachedFields || {}, safe);
    }

    // --- UI handlers ----------------------------------------------------------
    pickPercent(e) {
        const btn = e.currentTarget;
        const pct = parseInt(btn.dataset.percent, 10);
        if (Number.isNaN(pct)) return;

        // UI selection
        const list = this.percentListTarget;
        list.querySelectorAll('.percent-box').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        // live text
        const host = 100 - pct;
        this.charityPercentTarget.textContent = `${pct}%`;
        this.giftPercentTarget.textContent = `${host}%`;

        // persist as host_split (0.5 for 50%, etc.)
        const hostSplit = Math.max(0, Math.min(1, 1 - (pct / 100)));
        this._stagePatch({ party: { host_split: hostSplit } });
        this._flushPendingPatch();
    }

    _rehydrateUIFrom(fields) {
        const p = (fields && fields.party) || {};

        this._suspendAutosave = true;

        // host_split → activate the right % button & update text
        const hostSplit = typeof p.host_split === 'number' ? p.host_split : 0.5;
        const charityPct = Math.round((1 - hostSplit) * 100);
        if (this.hasPercentListTarget) {
        const btn = this.percentListTarget?.querySelector(`[data-percent="${charityPct}"]`);
            if (btn) {
                this.percentListTarget.querySelectorAll('.percent-box').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
            }
        }
        this.charityPercentTarget.textContent = `${charityPct}%`;
        this.giftPercentTarget.textContent    = `${100 - charityPct}%`;

        // contribution message / gifts / additional_information
        if (this.groupGiftingTarget) this.groupGiftingTarget.value = p.contributions_where_to || '';
        // if (this.groupGiftTarget)    this.groupGiftTarget.value    = p.gifts || '';
        if (this.moreInfoTarget)     this.moreInfoTarget.value     = p.additional_information || '';

        // options
        if (Array.isArray(p.party_option_ids)) {
            const set = new Set(p.party_option_ids.map(String));
            this.element.querySelectorAll('input[name="party_option_ids[]"]').forEach(cb => {
                cb.checked = set.has(cb.value);
            });
        }
        // live preview hydration
        this._updateContributionPreview(p.contributions_where_to || '');
        this._updateGiftsInline(p.gifts || '');
        this._suspendAutosave = false;

        // Ensure the server has at least the default host_split (0.5) if none was stored yet
        if (p.host_split == null) {
            this._stagePatch({ party: { host_split: 0.5 } });
        }
    }

    // text fields (group gifting / more info)
    onGroupGiftingInput(e) {
        const v = (e.target.value || "").trim();
        this._stagePatch({ party: { contributions_where_to: v } });
        // live UI update (no network)
        this._updateContributionPreview(v);
    }
    onGroupGiftInput(e) {
        const v = (e.target.value || "").trim();
        this._stagePatch({ party: { gifts: v } });
        // live UI update (no network)
        this._updateGiftsInline(v);
    }
    onMoreInfoInput(e) {
        this._stagePatch({ party: { additional_information: (e.target.value || '').trim() } });
    }

    // party options (checkboxes)
    onOptionToggle(e) {
        const boxes = this.element.querySelectorAll('input[name="party_option_ids[]"]');
        // Always compute the FULL selection to avoid incremental races
        const ids = Array.from(boxes).filter(b => b.checked).map(b => b.value);

        // Stage a cloned array so we don't mutate the same reference later
        this._stagePatch({ party: { party_option_ids: ids.slice() } });

        // Queue a flush that is race-safe (drains after in-flight finishes)
        this._queueFlush();
    }

    back() {
        const sgid = this.hasDraftSgidValue && this.draftSgidValue
            ? `draft_sgid=${encodeURIComponent(this.draftSgidValue)}`
            : `draft_id=${encodeURIComponent(this.draftIdValue)}`;
        const slug = this.hasInvitationSlugValue ? `&invitation=${encodeURIComponent(this.invitationSlugValue)}` : "";
        window.location.assign(`/party/charity?${sgid}${slug}`);
    }

    async continue() {
        // Persist latest staged deltas first (flush-once)
        await this._flushPendingPatch();

        if (this.contextValue === "party" && this.hasPartyTokenValue) {
            this._toast('Saving changes…', 'info', { persist: true });
            try {
                const res = await this._saveFieldsNow(this._cachedFields || {});
                if (!res.ok) throw new Error(await res.text());
                this._toast('Saved!', 'success', { delayClose: 700 });
                // Straight to manage invitation
                window.location.assign(`/dashboard/manage/${encodeURIComponent(this.partyTokenValue)}`);
            } catch (e) {
                console.warn("party save failed:", e);
                this._toast('Something went wrong, please try again', 'error');
            }
            return;
        }

        // Advance status -> review (change path/status if your next step differs)
        this._toast('Saving additional info...', 'info', { persist: true });
        try {
            const res = await fetch(`/party_drafts/${this.draftIdValue}`, {
                method: "PATCH",
                headers: this._headers(),
                body: JSON.stringify({ status: "review" })
            });
            this._toast('Preparing review…', 'success', { delayClose: 600 });
            // Navigate to next step
            const nextParams = this.hasDraftSgidValue && this.draftSgidValue
                ? `draft_sgid=${encodeURIComponent(this.draftSgidValue)}`
                : `draft_id=${encodeURIComponent(this.draftIdValue)}`;
            window.location.assign(`/party/review?${nextParams}`);
        } catch (e) {
            console.warn("advance failed:", e);
            this._toast('Something went wrong, please try again', 'error');
        }
    }

    _toast(title, icon = 'info', opts = {}) {
        const { persist = false, delayClose = 0 } = opts;
        Swal.fire({
            toast: true,
            icon,
            title,
            position: 'bottom-end',
            showConfirmButton: false,
            timer: persist ? undefined : 2200,
            timerProgressBar: !persist,
        });
        if (persist && delayClose) {
            setTimeout(() => Swal.close(), delayClose);
        }
    }

    // --- Rehydrate & wiring ---------------------------------------------------

    async _primeFieldsCache() {
        if (this._cachedFields) return;
        // Prefer data-value if present (fast), else GET /party_drafts/:id
        if (this.hasFieldsValue && this.fieldsValue && Object.keys(this.fieldsValue).length) {
            this._cachedFields = JSON.parse(JSON.stringify(this.fieldsValue));
            return;
        }
        if (this.hasDraftIdValue && this.draftIdValue) {
            try {
                const res = await fetch(`/party_drafts/${this.draftIdValue}`);
                if (res.ok) {
                    const j = await res.json();
                    this._cachedFields = j?.fields_json || {};
                    return;
                }
            } catch (_) {}
        }
        this._cachedFields = {};
    }

    async _saveFieldsNow(payload) {
        if (this.contextValue === "party" && this.hasPartyTokenValue) {
            return fetch(`/party/${encodeURIComponent(this.partyTokenValue)}/fields`, {
                method: "PATCH",
                headers: this._headers(),
                body: JSON.stringify({ fields_json: payload })
            });
        }
        if (this.contextValue === "draft" && this.hasDraftIdValue) {
            return fetch(`/party_drafts/${this.draftIdValue}`, {
                method: "PATCH",
                headers: this._headers(),
                body: JSON.stringify({fields_json: payload})
            });
        }
    }

    _rehydrateFromFields(fields) {
        const party = (fields && fields.party) || {};

        // Percent / host_split
        let pct = 50; // default
        if (typeof party.host_split === "number") {
            // host_split = 1 - charity%; charity% = (1 - host_split) * 100
            pct = Math.round((1 - party.host_split) * 100);
            pct = [20, 30, 40, 50,60,70,80,90,100].includes(pct) ? pct : 50;
        }
        this._setActivePercent(pct);
        this._updateInfoPercents(pct);

        // Contribution message
        if (this.hasGroupGiftingTarget && party.contributions_where_to != null) {
            this.groupGiftingTarget.value = String(party.contributions_where_to || "");
        }
        // Gift
        if (this.hasGroupGiftTarget && party.gifts != null) {
            this.groupGiftTarget.value = String(party.gifts || "");
        }
        // More information
        if (this.hasMoreInfoTarget && party.additional_information != null) {
            this.moreInfoTarget.value = String(party.additional_information || "");
        }
        // Party options
        if (this.hasOptionCheckboxTarget && Array.isArray(party.party_option_ids)) {
            const chosen = new Set(party.party_option_ids.map(String));
            this.optionCheckboxTargets.forEach(cb => {
                cb.checked = chosen.has(String(cb.value));
            });
        }
    }

    _wireInputs() {
        // Text inputs (debounced)
        if (this.hasGroupGiftingTarget) {
            this.groupGiftingTarget.addEventListener("input", () => {
                this._mergeAndPatch(
                    { party: { contributions_where_to: this.groupGiftingTarget.value || "" } },
                    true
                );
            });
        }
        if (this.hasGroupGiftTarget) {
            this.groupGiftTarget.addEventListener("input", () => {
                this._mergeAndPatch(
                    { party: { gifts: this.groupGiftTarget.value || "" } },
                    true
                );
            });
        }
        if (this.hasMoreInfoTarget) {
            this.moreInfoTarget.addEventListener("input", () => {
                this._mergeAndPatch(
                    { party: { additional_information: this.moreInfoTarget.value || "" } },
                    true
                );
            });
        }
        // Party options
        if (this.hasOptionCheckboxTarget) {
            this.optionCheckboxTargets.forEach(cb => {
                cb.addEventListener("change", () => {
                    const ids = this.optionCheckboxTargets
                        .filter(x => x.checked)
                        .map(x => parseInt(x.value, 10))
                        .filter(n => !isNaN(n));
                    this._mergeAndPatch({ party: { party_option_ids: ids } });
                });
            });
        }
    }

    // --- UI helpers -----------------------------------------------------------

    _setActivePercent(pct) {
        if (!this.hasPercentListTarget) return;
        const buttons = this.percentListTarget.querySelectorAll('[data-percent]');
        buttons.forEach(b => b.classList.remove('active'));
        const active = this.percentListTarget.querySelector(`[data-percent="${pct}"]`);
        if (active) active.classList.add('active');
    }

    _updateInfoPercents(charityPct) {
        const giftPct = 100 - (parseInt(charityPct, 10) || 0);
        if (this.hasCharityPercentTarget) this.charityPercentTarget.textContent = `${charityPct}%`;
        if (this.hasGiftPercentTarget)    this.giftPercentTarget.textContent    = `${giftPct}%`;
    }


    _queueFlush() {
        if (this._isFlushing) {
            // Mark that we need another pass once the current round completes
            this._flushAgain = true;
            return;
        }
        this._flushPendingPatch();
    }

    async _flushPendingPatch() {
        if (this._isFlushing) { this._flushAgain = true; return; }
        if (!this.pendingPatch) return;

        this._isFlushing = true;
        this._flushAgain = false;

        try {
            // Drain any accumulated deltas. New patches staged during await will be picked up.
            while (this.pendingPatch) {
                const toSend = this.pendingPatch;
                this.pendingPatch = null;

                const res = await this._saveFieldsNow(toSend);

                // const res = await fetch(`/party_drafts/${this.draftIdValue}`, {
                //     method: 'PATCH',
                //     headers: this._headers(),
                //     body: JSON.stringify({ fields_json: toSend })
                // });

                if (res && res.ok) {
                    // Merge what we just sent into local cache
                    this._cachedFields = this._mergeDeep(this._cachedFields || {}, toSend);

                    // Keep the data-* attribute (if present) in sync
                    const mainContainer = this.element.closest('[data-additional-info-fields-value]');
                    if (mainContainer) {
                        mainContainer.setAttribute(
                            'data-additional-info-fields-value',
                            JSON.stringify(this._cachedFields)
                        );
                    }
                }
                // loop continues if something else was staged during the await above
            }
        } catch (_e) {
            // swallow transient network errors; a later interaction will retry
        } finally {
            this._isFlushing = false;

            // If something arrived while we were finishing up, run again.
            if (this._flushAgain || this.pendingPatch) {
                this._flushAgain = false;
                // Use a microtask to yield to the UI thread
                setTimeout(() => this._flushPendingPatch(), 0);
            }
        }
    }
    _setupAutosave() {
        this._debouncedFlush = this._debounce(() => this._queueFlush(), 700);
    }

    // --- Persistence (safe merge under party) --------------------------------

    async _mergeAndPatch(patch, debounced = false) {
        // Re-fetch before patch (avoid stomping other tabs/autosaves)
        const latest = await this._fetchServerFields();
        const base   = this._mergeDeep(latest, this._cachedFields || {});
        // Force nesting under party only for this page
        const merged = this._mergeDeep(base, patch);

        this._cachedFields = merged;
        return this._save();
    }

    // async _save() {
    //     if (!(this.hasDraftIdValue && this.draftIdValue)) return;
    //
    //     try {
    //         const res = await fetch(`/party_drafts/${this.draftIdValue}`, {
    //             method: "PATCH",
    //             headers: this._headers(),
    //             body: JSON.stringify({ fields_json: this._cachedFields })
    //         });
    //         if (!res.ok) {
    //             // do not clear cache; keep last good copy in memory
    //             console.warn("save failed", await res.text());
    //         }
    //     } catch (e) {
    //         console.warn("save error", e);
    //     }
    // }
    async _save() {
        try {
            const res = await this._saveFieldsNow(this._cachedFields || {});
            if (!res?.ok) {
                console.warn("save failed", res && (await res.text()).slice(0, 200));
            }
        } catch (e) {
            console.warn("save error", e);
        }
    }

    _updateGiftsInline(v) {
        const gift = (v || "").trim();

        if (this.hasGiftsInlineTarget) {
            this.giftsInlineTarget.textContent = gift;
        }

        if (this.hasPreviewGiftLineTarget) {
            const el = this.previewGiftLineTarget;
            const label = el.dataset.childLabel || "Your child";
            const count = parseInt(el.dataset.childCount || "1", 10);
            const verb = count === 1 ? "is" : "are";

            if (gift) {
                el.textContent = `${label} ${verb} saving up for "${gift}"`;
            } else {
                el.textContent = "";
            }
        }

        this._updateBannerVisibility();
    }


    _updateContributionPreview(v) {
        if (!this.hasPreviewContributionMessageTarget) return;
        const el  = this.previewContributionMessageTarget;
        const txt = (v || "").trim();

        el.textContent = txt;          // <- actually update the DOM
        this._updateBannerVisibility();
    }

    _updateBannerVisibility() {
        if (!this.hasBannerTextContainerTarget) return;

        const hasContribution =
            this.hasPreviewContributionMessageTarget &&
            this.previewContributionMessageTarget.textContent.trim().length > 0;

        const hasGiftLine =
            this.hasPreviewGiftLineTarget &&
            this.previewGiftLineTarget.textContent.trim().length > 0;

        const shouldShow = hasContribution || hasGiftLine;
        this.bannerTextContainerTarget.classList.toggle("d-none", !shouldShow);
    }

    async _fetchServerFields() {
        try {
            if (this.contextValue === "party" && this.hasPartyTokenValue) {
                const res = await fetch(`/party/${encodeURIComponent(this.partyTokenValue)}/fields`);
                if (!res.ok) return {};
                const j = await res.json();
                return j?.fields_json || {};
            }

            if (this.hasDraftIdValue && this.draftIdValue) {
                const res = await fetch(`/party_drafts/${this.draftIdValue}`);
                if (!res.ok) return {};
                const j = await res.json();
                return j?.fields_json || {};
            }

            return {};
        } catch (_) {
            return {};
        }
    }

    _mergeDeep(target, source) {
        const out = Array.isArray(target) ? target.slice() : { ...(target || {}) };
        if (source && typeof source === "object") {
            for (const [k, v] of Object.entries(source)) {
                if (v && typeof v === "object" && !Array.isArray(v)) {
                    out[k] = this._mergeDeep(out[k] || {}, v);
                } else {
                    out[k] = v;
                }
            }
        }
        return out;
    }

    _headers() {
        return {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        };
    }

    _debounce(fn, ms) {
        let t;
        return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
    }
}